import { convertFiltersToApiParams } from '@features/ui-filters/api/filterUtils';
import { CreateForumPayload } from '@root/src/entities/DedicatedForum/schema';
import { dedicatedForumFromBack } from '@root/src/entities/DedicatedForum/responseFormatters';
import {
  DedicatedForumBack,
  DedicatedForumFront,
  DedicatedForumParticipantBack,
  DedicatedForumParticipantFront,
  DedicatedForumParticipantsResponseBack,
  DedicatedForumInvitationsResponseBack,
  DedicatedForumInviteResponseBack,
  DedicatedForumInvitedUserFront,
  UpdateDedicatedForumFront,
  DedicatedForumFileBack,
  DedicatedForumFileFront,
} from '@root/src/entities/DedicatedForum/types';
import {
  DedicatedForumMessageBack,
  DedicatedForumMessageFront,
  CreateDedicatedForumMessageFront,
} from '@root/src/entities/DedicatedForum/Message/type';
import {
  messageFromBack,
  fileFromBack,
  invitedUserFromBack,
  participantFromBack,
} from '@root/src/entities/DedicatedForum/responseFormatters';
import { SstFront, SstBack } from '@entities/Sst/type';
import { sstFromBack } from '@root/src/entities/responseFormatters';
import { PaginatedOptions, PaginatedResponse } from '@root/src/types/common';
import { mapPaginatedResponse } from '@root/src/utils/formatters/responseAdapters';
import { fetchRequest, RequestMethod, callApiBlob } from '@services/api/utils/utils';
import config from '@root/src/config';
import FetchError from '@services/api/utils/FetchError';

const getDedicatedForums = async ({
  filters = {},
  page = 1,
  pageSize = 15,
  sort = { field: 'createdAt', order: 'DESC' },
}: PaginatedOptions = {}): Promise<PaginatedResponse<DedicatedForumFront>> =>
  fetchRequest<PaginatedResponse<DedicatedForumBack>, PaginatedResponse<DedicatedForumFront>>({
    endpoint: '/dedicated-forums',
    method: RequestMethod.GET,
    query: convertFiltersToApiParams(filters, {
      page,
      pageSize,
      sorted: [
        {
          id: sort.field,
          desc: sort.order,
        },
      ],
    }),
    responseFormatter: data => mapPaginatedResponse(data, dedicatedForumFromBack),
  });

const getDedicatedForum = async (id: string): Promise<DedicatedForumFront> => {
  const result = await fetchRequest<DedicatedForumBack, DedicatedForumFront>({
    endpoint: `/dedicated-forums/${id}`,
    method: RequestMethod.GET,
    responseFormatter: dedicatedForumFromBack,
  });
  return result;
};

const createDedicatedForum = async (data: CreateForumPayload): Promise<DedicatedForumFront> =>
  fetchRequest<DedicatedForumBack, DedicatedForumFront, CreateForumPayload>({
    endpoint: '/dedicated-forums',
    method: RequestMethod.POST,
    body: data,
    responseFormatter: dedicatedForumFromBack,
  });

const updateDedicatedForum = async (id: string, data: UpdateDedicatedForumFront): Promise<DedicatedForumFront> =>
  fetchRequest<DedicatedForumBack, DedicatedForumFront, UpdateDedicatedForumFront>({
    endpoint: `/dedicated-forums/${id}`,
    method: RequestMethod.PUT,
    body: data,
    responseFormatter: dedicatedForumFromBack,
  });

const archiveDedicatedForum = async (id: string): Promise<DedicatedForumFront> =>
  fetchRequest<DedicatedForumBack, DedicatedForumFront>({
    endpoint: `/dedicated-forums/${id}/archive`,
    method: RequestMethod.PUT,
    responseFormatter: dedicatedForumFromBack,
  });

// Participation endpoints
const joinForum = async (forumId: string): Promise<DedicatedForumParticipantFront> =>
  fetchRequest<DedicatedForumParticipantBack, DedicatedForumParticipantFront>({
    endpoint: `/dedicated-forums/${forumId}/join`,
    method: RequestMethod.POST,
    responseFormatter: participantFromBack,
  });

const leaveForum = async (forumId: string): Promise<void> =>
  fetchRequest<void, void>({
    endpoint: `/dedicated-forums/${forumId}/leave`,
    method: RequestMethod.DELETE,
  });

const acceptAccessRequest = async (forumId: string, userId: string): Promise<DedicatedForumParticipantFront> =>
  fetchRequest<DedicatedForumParticipantBack, DedicatedForumParticipantFront>({
    endpoint: `/dedicated-forums/${forumId}/participants/${userId}/accept`,
    method: RequestMethod.POST,
    responseFormatter: participantFromBack,
  });

const updateParticipantRole = async (
  forumId: string,
  userId: string,
  isAdmin: boolean,
): Promise<DedicatedForumParticipantFront> =>
  fetchRequest<DedicatedForumParticipantBack, DedicatedForumParticipantFront, { isAdmin: boolean }>({
    endpoint: `/dedicated-forums/${forumId}/participants/${userId}/role`,
    method: RequestMethod.PUT,
    body: { isAdmin },
    responseFormatter: participantFromBack,
  });

const removeParticipant = async (forumId: string, userId: string): Promise<void> =>
  fetchRequest<void, void>({
    endpoint: `/dedicated-forums/${forumId}/participants/${userId}`,
    method: RequestMethod.DELETE,
  });

const inviteParticipants = async (
  forumId: string,
  userIds: string[],
  isAdmin: boolean = false,
): Promise<DedicatedForumInviteResponseBack> =>
  fetchRequest<
    DedicatedForumInviteResponseBack,
    DedicatedForumInviteResponseBack,
    { userIds: string[]; isAdmin: boolean }
  >({
    endpoint: `/dedicated-forums/${forumId}/invite`,
    method: RequestMethod.POST,
    body: { userIds, isAdmin },
  });

const getForumParticipants = async (forumId: string): Promise<DedicatedForumParticipantFront[]> => {
  const result = await fetchRequest<DedicatedForumParticipantsResponseBack, DedicatedForumParticipantFront[]>({
    endpoint: `/dedicated-forums/${forumId}/participants`,
    method: RequestMethod.GET,
    responseFormatter: (data: DedicatedForumParticipantsResponseBack) => data.map(participantFromBack),
  });
  return result;
};

const getForumInvitations = async (forumId: string): Promise<DedicatedForumInvitedUserFront[]> => {
  const result = await fetchRequest<DedicatedForumInvitationsResponseBack, DedicatedForumInvitedUserFront[]>({
    endpoint: `/dedicated-forums/${forumId}/invitations`,
    method: RequestMethod.GET,
    responseFormatter: (data: DedicatedForumInvitationsResponseBack) => data.map(invitedUserFromBack),
  });
  return result;
};

// Message endpoints
const getForumMessages = async (
  forumId: string,
  options: { from?: string; limit?: number } = {},
): Promise<DedicatedForumMessageFront[]> =>
  fetchRequest<DedicatedForumMessageBack[], DedicatedForumMessageFront[]>({
    endpoint: `/dedicated-forums/${forumId}/messages`,
    method: RequestMethod.GET,
    query: options,
    responseFormatter: data => data.map(messageFromBack),
  });

const createForumMessage = async (
  forumId: string,
  message: CreateDedicatedForumMessageFront,
): Promise<DedicatedForumMessageFront> =>
  fetchRequest<DedicatedForumMessageBack, DedicatedForumMessageFront, CreateDedicatedForumMessageFront>({
    endpoint: `/dedicated-forums/${forumId}/messages`,
    method: RequestMethod.POST,
    body: message,
    responseFormatter: messageFromBack,
  });

const getForumMessage = async (forumId: string, messageId: string): Promise<DedicatedForumMessageFront> =>
  fetchRequest<DedicatedForumMessageBack, DedicatedForumMessageFront>({
    endpoint: `/dedicated-forums/${forumId}/messages/${messageId}`,
    method: RequestMethod.GET,
    responseFormatter: messageFromBack,
  });

const getForumMessageReplies = async (
  forumId: string,
  messageId: string,
  options: { from?: string; limit?: number } = {},
): Promise<DedicatedForumMessageFront[]> =>
  fetchRequest<DedicatedForumMessageBack[], DedicatedForumMessageFront[]>({
    endpoint: `/dedicated-forums/${forumId}/messages/${messageId}/replies`,
    method: RequestMethod.GET,
    query: options,
    responseFormatter: data => data.map(messageFromBack),
  });

const createForumMessageReply = async (
  forumId: string,
  messageId: string,
  reply: CreateDedicatedForumMessageFront,
): Promise<DedicatedForumMessageFront> =>
  fetchRequest<DedicatedForumMessageBack, DedicatedForumMessageFront, CreateDedicatedForumMessageFront>({
    endpoint: `/dedicated-forums/${forumId}/messages/${messageId}/replies`,
    method: RequestMethod.POST,
    body: reply,
    responseFormatter: messageFromBack,
  });

const deleteForumMessage = async (forumId: string, messageId: string): Promise<DedicatedForumMessageFront> =>
  fetchRequest<DedicatedForumMessageBack, DedicatedForumMessageFront>({
    endpoint: `/dedicated-forums/${forumId}/messages/${messageId}`,
    method: RequestMethod.DELETE,
    responseFormatter: messageFromBack,
  });

// File endpoints
const getForumFiles = async (
  forumId: string,
  options: { page?: number; pageSize?: number } = {},
): Promise<PaginatedResponse<DedicatedForumFileFront>> => {
  const { page = 1, pageSize = 10 } = options;

  // The API returns { items, total, page, pageSize }
  // We need to adapt it to PaginatedResponse format { items, pages, total }
  interface FilesApiResponse {
    items: DedicatedForumFileBack[];
    total: number;
    page: number;
    pageSize: number;
  }

  return fetchRequest<FilesApiResponse, PaginatedResponse<DedicatedForumFileFront>>({
    endpoint: `/dedicated-forums/${forumId}/files`,
    method: RequestMethod.GET,
    query: { page, pageSize },
    responseFormatter: data => ({
      items: data.items.map(fileFromBack),
      pages: Math.ceil(data.total / data.pageSize),
      total: data.total,
    }),
  });
};

const downloadForumFile = async (forumId: string, fileId: string): Promise<Blob> => {
  const endpoint = `/dedicated-forums/${forumId}/files/${fileId}/download/`;
  return callApiBlob(endpoint, RequestMethod.GET);
};

const uploadForumFile = async (forumId: string, messageId: string, file: File): Promise<DedicatedForumFileFront> => {
  const formData = new FormData();
  // Append file with field name 'file' as required by the API
  formData.append('file', file, file.name);

  // Use fetch directly for file uploads to ensure FormData is handled correctly
  // Endpoint: POST /back/dedicated-forums/:id/messages/:messageId/files
  const url = `${config.url.urlApi}/dedicated-forums/${forumId}/messages/${messageId}/files`;

  const response = await fetch(url, {
    method: 'POST',
    body: formData,
    credentials: 'include', // Sends cookies for authentication
    // Don't set Content-Type header - browser will set it automatically with multipart/form-data boundary
  });

  if (!response.ok) {
    let errorMessage = response.statusText;
    let errorData: any;
    try {
      const errorText = await response.text();
      errorData = errorText ? JSON.parse(errorText) : undefined;
      if (errorData.message) {
        errorMessage = errorData.message;
      }
      if (errorData.error && !errorData.message) {
        errorMessage = errorData.error;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new FetchError(response, errorMessage, errorData?.code, {
      ...errorData,
      status: errorData?.status ?? response.status,
    });
  }

  const data = (await response.json()) as DedicatedForumFileBack;
  return fileFromBack(data);
};

// SST blocks endpoints
const getSstBlocks = async (forumId: string): Promise<SstFront[]> =>
  fetchRequest<SstBack[], SstFront[]>({
    endpoint: `/dedicated-forums/${forumId}/sst-blocks`,
    method: RequestMethod.GET,
    responseFormatter: (data: SstBack[]) => data.map(sstFromBack),
  });

const publishSstBlock = async (forumId: string, sstId: string): Promise<void> =>
  fetchRequest<void, void, { sstId: string }>({
    endpoint: `/dedicated-forums/${forumId}/sst-blocks`,
    method: RequestMethod.POST,
    body: { sstId },
  });

const removeSstBlock = async (forumId: string, sstId: string): Promise<void> =>
  fetchRequest<void, void>({
    endpoint: `/dedicated-forums/${forumId}/sst-blocks/${sstId}`,
    method: RequestMethod.DELETE,
  });

// Picture upload endpoint
const uploadForumPicture = async (forumId: string, pictureFile: File): Promise<DedicatedForumFront> => {
  const formData = new FormData();
  // Append file with field name 'picture' as required by the API
  formData.append('picture', pictureFile);

  // Use fetch directly for file uploads to ensure FormData is handled correctly
  // Endpoint: POST /back/dedicated-forums/:id/picture
  const url = `${config.url.urlApi}/dedicated-forums/${forumId}/picture`;

  const response = await fetch(url, {
    method: 'POST',
    body: formData,
    credentials: 'include', // Sends cookies for authentication
    // Don't set Content-Type header - browser will set it automatically with multipart/form-data boundary
  });

  if (!response.ok) {
    let errorMessage = response.statusText;
    let errorData: any;
    try {
      const errorText = await response.text();
      errorData = errorText ? JSON.parse(errorText) : undefined;
      if (errorData.message) {
        errorMessage = errorData.message;
      }
      if (errorData.error && !errorData.message) {
        errorMessage = errorData.error;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new FetchError(response, errorMessage, errorData?.code, {
      ...errorData,
      status: errorData?.status ?? response.status,
    });
  }

  const data = (await response.json()) as DedicatedForumBack;
  return dedicatedForumFromBack(data);
};

// Note: joinForum, leaveForum, requestForumAccess, and getParticipants
// are not yet implemented in the backend API

export const dedicatedForumsApi = {
  getDedicatedForums,
  getDedicatedForum,
  createDedicatedForum,
  updateDedicatedForum,
  archiveDedicatedForum,
  joinForum,
  leaveForum,
  acceptAccessRequest,
  updateParticipantRole,
  removeParticipant,
  inviteParticipants,
  getForumParticipants,
  getForumInvitations,
  // Message endpoints
  getForumMessages,
  createForumMessage,
  getForumMessage,
  getForumMessageReplies,
  createForumMessageReply,
  deleteForumMessage,
  // File endpoints
  getForumFiles,
  downloadForumFile,
  uploadForumFile,
  // SST blocks endpoints
  getSstBlocks,
  publishSstBlock,
  removeSstBlock,
  // Picture upload endpoint
  uploadForumPicture,
};
